import { ArrowRight, CircleAlert, CircleCheck, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { usePermissions } from "@/lib/auth/usePermissions";
import { cn } from "@/lib/utils";

import { rowsFrom, type TodoSource } from "./todo";
import type { TodoSources } from "./useTodoSources";

function Skeleton() {
  return (
    <li aria-hidden className="flex min-h-16 items-center gap-3 rounded-md border p-3">
      <span className="size-8 animate-pulse rounded-full bg-muted" />
      <span className="h-4 flex-1 animate-pulse rounded bg-muted" />
      <span className="h-9 w-24 animate-pulse rounded bg-muted" />
    </li>
  );
}

/**
 * "What needs doing today" (T11-10): one row per kind of work — count + a big "Do it" that opens
 * the screen already filtered. Rows of a service that did not answer show a small "could not
 * check — try again" line instead (the others still work).
 */
export function TodoInbox({ sources }: { sources: TodoSources }) {
  const { t } = useTranslation("dashboard");
  const perms = usePermissions();
  const rows = rowsFrom(sources.answers, perms.can);
  const entries = Object.entries(sources.states) as Array<
    [TodoSource, NonNullable<TodoSources["states"][TodoSource]>]
  >;
  const loading = entries.some(([, s]) => s.loading);
  const failed = entries.filter(([, s]) => s.error);
  const nothingAllowed = entries.length === 0;

  return (
    <section aria-labelledby="todo-title" data-slot="todo-inbox" className="flex flex-col gap-3">
      <h2 id="todo-title" className="text-lg font-bold">
        {t("todo.title")}
      </h2>
      <ul className="flex flex-col gap-2" aria-busy={loading}>
        {rows.map((row) => (
          <li
            key={row.key}
            data-todo={row.key}
            className={cn(
              "flex min-h-16 flex-wrap items-center gap-3 rounded-md border p-3",
              row.tone === "problem" ? "border-[#b91c1c]" : "border-[#b45309]",
            )}
          >
            {row.tone === "problem" ? (
              <CircleAlert className="size-6 shrink-0 text-[#b91c1c]" aria-hidden />
            ) : (
              <Clock className="size-6 shrink-0 text-[#b45309]" aria-hidden />
            )}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="font-semibold">
                {t(`todo.rows.${row.key}`, { count: row.count.n })}
                {row.count.more ? "+" : ""}
              </span>
              <span className="text-sm text-muted-foreground">{t(`todo.hints.${row.key}`)}</span>
            </span>
            <Link
              to={row.to}
              className="inline-flex min-h-touch items-center gap-2 rounded-md bg-foreground px-4 font-semibold text-background hover:opacity-90"
              aria-label={`${t("todo.do_it")} — ${t(`todo.rows.${row.key}`, { count: row.count.n })}`}
            >
              {t("todo.do_it")}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </li>
        ))}
        {loading && rows.length === 0 ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : null}
      </ul>
      {!loading && rows.length === 0 && failed.length === 0 && !nothingAllowed ? (
        <p className="flex items-center gap-2 rounded-md border p-3 text-sm" data-slot="todo-empty">
          <CircleCheck className="size-5 text-[#047857]" aria-hidden />
          {t("todo.all_done")}
        </p>
      ) : null}
      {failed.length > 0 ? (
        <div className="flex flex-col gap-1" role="group" aria-label={t("todo.could_not_check")}>
          {failed.map(([source, s]) => (
            <div
              key={source}
              role="alert"
              data-source-error={source}
              className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2 text-sm"
            >
              <CircleAlert className="size-4 text-status-problem" aria-hidden />
              <span className="flex-1">{t(`todo.source_error.${source}`)}</span>
              <button
                type="button"
                onClick={s.refetch}
                className="min-h-touch rounded-md border px-3 text-sm font-medium hover:bg-accent"
              >
                {t("todo.retry")}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
